import { type ReactElement, type ReactNode, useId } from "react";
import {
  Box,
  CircularProgress,
  Divider,
  FormControl,
  FormHelperText,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  SelectChangeEvent,
  type SelectProps,
  type SxProps,
  Typography,
} from "@mui/material";

import {
  type Control,
  type FieldValues,
  type Path,
  useController,
} from "react-hook-form";

type Option = {
  id: string;
  isDivider?: boolean;
  disabled?: boolean;
  padding?: number;
  label?: string;
  secondaryLabel?: ReactNode;
};

type SelectInputProps<TForm extends FieldValues, TOption extends Option> = {
  fieldName: Path<TForm>;
  label: string;
  hideLabel?: boolean;
  hideError?: boolean;
  control: Control<TForm>;
  size?: SelectProps["size"];
  optionLabelField?: keyof TOption;
  disabled?: boolean;
  placeholder?: string;
  options: TOption[];
  hide?: boolean;
  isLoading?: boolean;
  onChange?: (event: SelectChangeEvent) => void;
  sx?: SxProps;
};

export const SelectInput = <TForm extends FieldValues, TOption extends Option>({
  fieldName,
  label,
  hideLabel = false,
  hideError = false,
  control,
  size,
  optionLabelField = "label",
  disabled,
  options,
  placeholder,
  hide,
  isLoading,
  onChange,
  sx,
}: SelectInputProps<TForm, TOption>): ReactElement | null => {
  const id = useId();

  const {
    field: { ...field },
    fieldState: { error },
  } = useController({
    name: fieldName,
    control,
  });

  if (hide) {
    return null;
  }

  return (
    <Box
      sx={[
        {
          display: "flex",
          flexDirection: "column",
          gap: 0.5,
          width: "100%",
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {!hideLabel && <Typography id={id}>{label}</Typography>}
      <FormControl fullWidth disabled={disabled} error={!!error} size={"small"}>
        <Select
          size={size}
          fullWidth
          {...field}
          labelId={hideLabel ? undefined : id}
          slotProps={{
            input: {
              "aria-label": hideLabel ? label : undefined,
            },
          }}
          onChange={(...args) => {
            field.onChange(...args);
            // Hook into change event to trigger side effects
            onChange?.(args[0]);
          }}
          endAdornment={
            isLoading ? (
              <CircularProgress size={20} sx={{ display: "flex" }} />
            ) : undefined
          }
          IconComponent={isLoading ? Box : undefined}
          value={options.find((option) => option.id === field.value)?.id ?? ""}
          displayEmpty
          renderValue={(value) =>
            value.length > 0
              ? (options
                  .find((option) => option.id === value)
                  ?.[optionLabelField]?.toString() ?? value)
              : placeholder
          }
        >
          {options.map((option) =>
            option.isDivider ? (
              <Divider key={option.id || "empty"} />
            ) : (
              <MenuItem
                key={option.id || "empty"}
                disabled={option.disabled}
                value={option.id}
                aria-label={(option[optionLabelField] ?? option.id)?.toString()}
              >
                <ListItem
                  component={"div"}
                  sx={{ padding: 0, paddingInlineStart: option.padding ?? 0 }}
                >
                  <ListItemText
                    primary={
                      <Typography variant="inherit" noWrap>
                        {(option[optionLabelField] ?? option.id)?.toString()}
                      </Typography>
                    }
                    secondary={option.secondaryLabel}
                  />
                </ListItem>
              </MenuItem>
            ),
          )}
        </Select>
        {!hideError && error?.message && (
          <FormHelperText>{error.message}</FormHelperText>
        )}
      </FormControl>
    </Box>
  );
};
